import { useEffect, useMemo } from 'react'
import CalendarRecommendationsNotAvailableMessage from './CalendarRecommendationsNotAvailableMessage'
import CalendarSimilarOfferTile from './CalendarSimilarOfferTile'
import { isCalendarYmalOffer } from './calendarYmalOffer'
import { useAppSelector } from 'hooks/reduxHooks'
import moment from 'moment'
import { buildSearchStringFromFilters } from 'lib/search/searchUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import CalendarPickerGridLayout from './CalendarPickerGridLayout'
import { getDistanceUnit } from 'selectors/recommendations'
import { getListName } from 'analytics/enhanced-ecommerce'
import { initialOfferPageRooms } from 'contexts/OfferPage/offerPageStateReducer'
import { take } from 'lib/array/arrayUtils'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import useOffers from 'hooks/Offers/useOffers'
import useCalendarRecommendations from 'hooks/Recommendation/useCalendarRecommendations'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import useStableObjectReference from 'hooks/useStableObjectReference'
import Group from 'components/utils/Group'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import IconButton from 'LuxKit/Button/IconButton'
import TopPicksForMeOfferSkeleton from 'components/Recommendations/TopPicksForMe/TopPicksForMeOfferSkeleton'
import { getAllOffers } from 'selectors/offerSelectors'
import { getCalendarSimilarDisplayOfferIds, MAX_CALENDAR_SIMILAR_CANDIDATE_COUNT, MAX_CALENDAR_SIMILAR_DISPLAY_COUNT } from './getCalendarSimilarDisplayOfferIds'
import { applyCalendarSimilarBedbankDisplayPrices } from './applyCalendarSimilarBedbankDisplayPrices'
import { useCalendarRecommendationsView } from '../CalendarRecommendationsViewContext'
import { getCanViewLuxPlusBenefits, getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'

interface Props {
  checkIn?: string
  monthIndex?: string
  duration: number
  offerType: App.OfferType
  offerId: string
  rooms?: Array<App.Occupants>
  seedOffer?: App.Offer | App.BedbankOffer
  onBackToMonth?: () => void
}

function getLereCalendarTrackingListName(offerType: App.OfferType) {
  const mapping: { [key in App.OfferType]?: string } = {
    hotel: 'FLASH',
    tactical_ao_hotel: 'LPC',
    bedbank_hotel: 'LPP',
  }
  const offerTypeLabel = mapping[offerType] ?? offerType
  return getListName('LERE', offerTypeLabel, 'Calendar V2.0.0')
}

const SKELETON_PLACEHOLDERS = [0, 1, 2, 3, 4, 5]
const NO_DATES_FILTER: App.OfferListFilters = {}

function CalendarPickerRecommendations({
  checkIn,
  duration,
  monthIndex,
  offerType,
  offerId,
  rooms,
  seedOffer,
  onBackToMonth,
}: Props) {
  const { setHasRenderableRecommendations } = useCalendarRecommendationsView()
  const distanceUnit = useAppSelector(getDistanceUnit)
  const defaultDepositAmountPercentage = useAppSelector(getDefaultDepositAmountPercentage)
  const canUseHotelMemberPrice = useAppSelector(getCanViewLuxPlusBenefits)
  const canUseBedbankMemberPrice = useAppSelector(getIsLuxPlusLPPEnabled)

  const hasExactCheckIn = Boolean(checkIn)
  const checkInISO = useMemo(() => hasExactCheckIn ? moment(checkIn).format(ISO_DATE_FORMAT) : undefined, [checkIn, hasExactCheckIn])
  const checkOutISO = useMemo(() => hasExactCheckIn ? moment(checkIn).add(duration, 'days').format(ISO_DATE_FORMAT) : undefined, [checkIn, duration, hasExactCheckIn])
  const occupancy = useStableObjectReference(rooms?.length ? rooms : initialOfferPageRooms)
  const { offerIds: lereOfferIds, fetching: isLereLoading } = useCalendarRecommendations(
    offerId,
    checkIn,
    monthIndex,
    duration,
    occupancy,
  )
  const candidateOfferIds = useMemo(
    () => take(lereOfferIds, MAX_CALENDAR_SIMILAR_CANDIDATE_COUNT),
    [lereOfferIds],
  )

  useOffers(candidateOfferIds)

  const allOffers = useAppSelector(getAllOffers)
  const offerErrors = useAppSelector(state => state.offer.offerErrors)
  const datedFilters = useMemo((): App.OfferListFilters => ({
    checkIn: checkInISO,
    checkOut: checkOutISO,
    rooms: occupancy,
  }), [checkInISO, checkOutISO, occupancy])
  const ymalOffers = useMemo(() => {
    const offers: Array<App.HotelOffer | App.BedbankOffer> = []
    for (const id of candidateOfferIds) {
      const offer = allOffers[id]
      if (offer && !offerErrors[id] && isCalendarYmalOffer(offer)) {
        offers.push(offer)
      }
    }
    return offers
  }, [allOffers, candidateOfferIds, offerErrors])
  const sharedBestPrices = useOffersBestPrices(ymalOffers, hasExactCheckIn ? datedFilters : NO_DATES_FILTER)
  const bedbankOfferRates = useAppSelector(state => state.offer.bedbankOfferRates)
  const bestPrices = useMemo(() => applyCalendarSimilarBedbankDisplayPrices({
    bestPrices: sharedBestPrices,
    offers: ymalOffers,
    bedbankOfferRates,
    filters: hasExactCheckIn ? datedFilters : NO_DATES_FILTER,
  }), [bedbankOfferRates, datedFilters, hasExactCheckIn, sharedBestPrices, ymalOffers])

  const { displayOfferIds, pendingCount } = useMemo(() => getCalendarSimilarDisplayOfferIds({
    candidateOfferIds,
    allOffers,
    offerErrors,
    hasExactCheckIn,
    bestPrices,
    canUseHotelMemberPrice,
    canUseBedbankMemberPrice,
  }), [allOffers, bestPrices, canUseBedbankMemberPrice, canUseHotelMemberPrice, candidateOfferIds, hasExactCheckIn, offerErrors])

  const remainingSkeletons = isLereLoading
    ? MAX_CALENDAR_SIMILAR_DISPLAY_COUNT
    : Math.min(MAX_CALENDAR_SIMILAR_DISPLAY_COUNT - displayOfferIds.length, pendingCount)

  const hasRenderableRecommendations = displayOfferIds.length > 0 || remainingSkeletons > 0

  useEffect(() => {
    setHasRenderableRecommendations(hasRenderableRecommendations)

    return () => {
      setHasRenderableRecommendations(false)
    }
  }, [hasRenderableRecommendations, setHasRenderableRecommendations])

  const tracking = useOfferListTracking(getLereCalendarTrackingListName(offerType))

  const linkQuery = useMemo(() => buildSearchStringFromFilters({
    propertyId: offerId,
    searchNearby: true,
    checkIn: checkInISO,
    checkOut: checkOutISO,
    rooms: occupancy,
  }), [occupancy, checkInISO, checkOutISO, offerId])

  const showEmpty = remainingSkeletons === 0 && displayOfferIds.length === 0

  return (
    <Group gap={8} direction="vertical">
      {onBackToMonth && <Group gap={8} direction="horizontal" verticalAlign="center">
        <IconButton
          kind="tertiary"
          size="small"
          onClick={onBackToMonth}
        >
          <LineArrowLeftIcon />
        </IconButton>
      </Group>}
      {showEmpty && <CalendarRecommendationsNotAvailableMessage />}
      {!showEmpty && <OfferListEventsProvider tracking={tracking}>
        <CalendarPickerGridLayout>
          {displayOfferIds.map((id, i) => (
            <CalendarSimilarOfferTile
              key={id}
              offerId={id}
              position={i}
              linkQuery={linkQuery}
              duration={duration}
              roomCount={occupancy.length}
              checkIn={checkInISO}
              checkOut={checkOutISO}
              seedOffer={seedOffer}
              distanceUnit={distanceUnit}
              defaultDepositAmountPercentage={defaultDepositAmountPercentage}
              hasExactCheckIn={hasExactCheckIn}
              alternativePrice={hasExactCheckIn ? bestPrices[id]?.bestPrice : undefined}
              alternativeMemberPrice={hasExactCheckIn ? bestPrices[id]?.bestMemberPrice : undefined}
              alternativeValue={hasExactCheckIn ? bestPrices[id]?.bestValue : undefined}
            />
          ))}
          {take(SKELETON_PLACEHOLDERS, remainingSkeletons).map((placeholder) => (
            <TopPicksForMeOfferSkeleton key={placeholder} />
          ))}
        </CalendarPickerGridLayout>
      </OfferListEventsProvider>}
    </Group>
  )
}

export default CalendarPickerRecommendations
